/*
 * The colour theme preference.
 *
 * The app follows the operating system by default; the override is stored per
 * device rather than per account, because "dark on my phone, light on the ward
 * PC" is a device-shaped preference, not a person-shaped one.
 *
 * The first resolution happens before React exists, in public/theme.js, so a
 * light-theme user never sees a dark flash. This module owns the preference
 * afterwards and applies changes through that script's own code path, so the
 * `data-theme` attribute and the theme-colour metas are only written in one
 * place. The storage key and the three allowed values are the contract between
 * the two files.
 */

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** What the preference resolved to. `system` is never a resolved value. */
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'iculogbook:theme';

function isPreference(value: string | null): value is ThemePreference {
  return value !== null && (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function getThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    // Safari in private mode throws on localStorage. Follow the OS instead.
    return 'system';
  }
}

export function resolveSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? resolveSystemTheme() : preference;
}

/*
 * public/theme.js hangs this on `window` so the attribute and the theme-colour
 * metas are written by one implementation. If the script were blocked the
 * fallback still sets the attribute, which is the part that actually restyles
 * the app; only the browser chrome colour would be stale.
 */
type ThemeGlobal = {
  __iculogbookApplyTheme?: (preference: ThemePreference) => ResolvedTheme;
};

function applyToDocument(preference: ThemePreference): ResolvedTheme {
  const apply = (window as unknown as ThemeGlobal).__iculogbookApplyTheme;

  if (typeof apply === 'function') return apply(preference);

  const resolved = resolveTheme(preference);
  document.documentElement.setAttribute('data-theme', resolved);
  return resolved;
}

export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Nothing to do: the theme still applies for this session.
  }

  return applyToDocument(preference);
}

/**
 * Calls back whenever the resolved theme changes — either because the
 * preference changed or, while following the system, because the OS did.
 * Returns the unsubscribe function.
 */
export function onThemeChange(listener: (theme: ResolvedTheme) => void): () => void {
  const query = window.matchMedia('(prefers-color-scheme: light)');

  const handle = (): void => {
    if (getThemePreference() === 'system') listener(resolveSystemTheme());
  };

  query.addEventListener('change', handle);

  /*
   * An explicit change is announced on the document rather than through a
   * module-level subscriber list, so anything that needs to re-read the tokens
   * — chartTheme.ts above all — can listen without importing this module.
   */
  const handleExplicit = (event: Event): void => {
    listener((event as CustomEvent<ResolvedTheme>).detail);
  };

  document.addEventListener(THEME_CHANGE_EVENT, handleExplicit);

  return () => {
    query.removeEventListener('change', handle);
    document.removeEventListener(THEME_CHANGE_EVENT, handleExplicit);
  };
}

export const THEME_CHANGE_EVENT = 'iculogbook:themechange';

/** Applies and stores the preference, then announces the resolved theme. */
export function changeTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = setThemePreference(preference);

  document.dispatchEvent(
    new CustomEvent<ResolvedTheme>(THEME_CHANGE_EVENT, { detail: resolved }),
  );

  return resolved;
}
